import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_ATTACHMENTS = 10

function getFileType(mimeType: string): 'image' | 'document' | 'audio' {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('audio/')) return 'audio'
  return 'document'
}

interface UploadAttachmentInput {
  recordId: string
  file: File
  organizationId: string
  patientId: string
}

interface DeleteAttachmentInput {
  attachmentId: string
  filePath: string
  recordId: string
  patientId: string
}

interface RetryTranscriptionInput {
  attachmentId: string
  filePath: string
  patientId: string
}

export function useUploadAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: UploadAttachmentInput) => {
      // Validate file size
      if (input.file.size > MAX_FILE_SIZE) {
        throw new Error('Arquivo excede o limite de 50MB')
      }

      // Check attachment count
      const { count, error: countError } = await supabase
        .from('record_attachments')
        .select('*', { count: 'exact', head: true })
        .eq('record_id', input.recordId)

      if (countError) throw countError
      if ((count ?? 0) >= MAX_ATTACHMENTS) {
        throw new Error('Limite de 10 anexos por registro atingido')
      }

      // Build storage path
      const timestamp = Date.now()
      const safeName = input.file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const filePath = `${input.organizationId}/${input.patientId}/${input.recordId}/${timestamp}-${safeName}`

      // 1. Upload file to storage
      const { error: uploadError } = await supabase.storage
        .from('patient-records')
        .upload(filePath, input.file, {
          contentType: input.file.type,
        })

      if (uploadError) throw uploadError

      // 2. Determine file type
      const fileType = getFileType(input.file.type)
      const isAudio = fileType === 'audio'

      // 3. Insert attachment record
      const { data: attachment, error: insertError } = await supabase
        .from('record_attachments')
        .insert({
          record_id: input.recordId,
          file_type: fileType,
          file_path: filePath,
          original_name: input.file.name,
          file_size: input.file.size,
          mime_type: input.file.type,
          transcription_status: isAudio ? 'pending' : null,
        })
        .select()
        .single()

      if (insertError) throw insertError

      // 4. If audio, trigger transcription
      if (isAudio) {
        const { error: fnError } = await supabase.functions.invoke(
          'transcribe-audio',
          {
            body: {
              attachment_id: attachment.id,
              file_path: filePath,
            },
          },
        )

        if (fnError) {
          console.error('Failed to trigger transcription:', fnError)
          // Update status to failed if invocation fails
          await supabase
            .from('record_attachments')
            .update({ transcription_status: 'failed' })
            .eq('id', attachment.id)
        }
      }

      return attachment
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-records', variables.patientId],
      })
    },
  })
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: DeleteAttachmentInput) => {
      // 1. Delete from storage
      const { error: storageError } = await supabase.storage
        .from('patient-records')
        .remove([input.filePath])

      if (storageError) throw storageError

      // 2. Delete from database
      const { error: dbError } = await supabase
        .from('record_attachments')
        .delete()
        .eq('id', input.attachmentId)

      if (dbError) throw dbError
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-records', variables.patientId],
      })
    },
  })
}

export function useRetryTranscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: RetryTranscriptionInput) => {
      // 1. Reset status to pending
      const { error: updateError } = await supabase
        .from('record_attachments')
        .update({ transcription_status: 'pending', transcription: null })
        .eq('id', input.attachmentId)

      if (updateError) throw updateError

      // 2. Re-invoke Edge Function
      const { error: fnError } = await supabase.functions.invoke(
        'transcribe-audio',
        {
          body: {
            attachment_id: input.attachmentId,
            file_path: input.filePath,
          },
        },
      )

      if (fnError) {
        // Mark as failed again
        await supabase
          .from('record_attachments')
          .update({ transcription_status: 'failed' })
          .eq('id', input.attachmentId)
        throw fnError
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-records', variables.patientId],
      })
    },
  })
}
